import React, { useState } from "react";
// MUI
import { alpha, Box, Stack, Typography } from "@mui/material";
// Component
import Page from "components/shared/Page";
import Form from "components/form";
import { ButtonContained } from "components/button/CommonButton";
import { FizenLink } from "components/link/FizenLink";

// Hooks
import useAuth from "hooks/useAuth";
import { PATH_AUTH } from "routes/path";
// Recoil
import { useRecoilValue } from "recoil";
import { userProfile } from "recoil/atom";
// Utils
import { IntegerInputFormat } from "utils/inputHandler";
// Package
import * as yup from "yup";
import { Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import FizenInput from "components/form/FizenInput";

const formModel = {
  email: {
    type: "string",
    label: "Email",
    name: "email",
    sx: {
      width: "100%",
      "& .MuiFilledInput-root": {
        transition: (theme) =>
          theme.transitions.create(["border-color", "background-color", "box-shadow"]),
        "&:hover": {
          backgroundColor: "white",
          borderColor: (theme) => theme.palette.warning.dark,
          boxShadow: (theme) => `${alpha(theme.palette.warning.dark, 0.25)} 0 0 0 2px`,
        },
        "&.Mui-focused": {
          backgroundColor: "white",
          borderColor: (theme) => theme.palette.warning.dark,
        },
      },
    },
  },
  password: {
    type: "password",
    label: "Password",
    name: "password",
    sx: {
      width: "100%",
      marginTop: 1.5,
      "& .MuiFilledInput-root": {
        transition: (theme) =>
          theme.transitions.create(["border-color", "background-color", "box-shadow"]),
        "&:hover": {
          backgroundColor: "white",
          borderColor: (theme) => theme.palette.warning.dark,
          boxShadow: (theme) => `${alpha(theme.palette.warning.dark, 0.25)} 0 0 0 2px`,
        },
        "&.Mui-focused": {
          backgroundColor: "white",
          borderColor: (theme) => theme.palette.warning.dark,
        },
      },
    },
  },
};

const validationSchema = yup.object().shape({
  email: yup.string().required("This field is required").email("Email is invalid"),
  password: yup.string().required("This field is required"),
});

const defaultValues = { email: "", password: "" };

const SignIn = () => {
  const { login } = useAuth();
  const { isAuthenticated, user } = useRecoilValue(userProfile);
  // State
  const [digitCode, setDigitCode] = useState("");
  const [userInput, setUserInput] = useState({ email: "", password: "" });
  const [backupCode, setBackUpCode] = useState("");
  const [mode, setMode] = useState("2fa");
  const form = useForm({
    defaultValues,
    resolver: validationSchema ? yupResolver(validationSchema) : undefined,
  });

  const handleChangeDigitCode = (e) => setDigitCode(IntegerInputFormat(e.target.value, 6));
  const handleChangeBackupCode = (e) => {
    const value = e.target.value;
    if (value.length <= 10) {
      setBackUpCode(value);
    }
  };

  const changeInputMode = (m) => (e) => {
    setMode(m);
    setDigitCode("");
    setBackUpCode("");
  };

  const handleConfirmCode = async () => {
    try {
      const data = {
        ...userInput,
        authCode: mode === "2fa" ? digitCode : backupCode,
      };
      await login(data);
    } catch (err) {
      console.log(err.message);
      form.setError("code", { message: err.message });
    }
  };

  const onFirstLogin = async (values) => {
    try {
      const data = { ...values, email: values.email?.trim() };
      setUserInput(data);
      await login(data);
    } catch (err) {
      console.log(err.message);
      form.setError("email", { message: err.message }, { shouldFocus: true });
      form.setError("password", { message: err.message });
    }
  };

  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <Page title="Sign In | Fizen Payment Gateway">
      <div className="flex w-full h-screen justify-center items-center flex-col gap-[12px] 2xl:gap-[24px]">
        <Typography sx={{ fontSize: 36, fontWeight: 600, color: "white" }}>Fizen Pay</Typography>
        <Box
          sx={{
            boxShadow: "rgb(0 0 0 / 6%) 0px 4px 4px, rgb(0 0 0 / 8%) 0px 8px 18px",
            width: { xs: 9 / 10, md: 650 },
            bgcolor: "background.grey",
            borderRadius: "9px",
          }}
          py={{ xs: 3, mac: 5 }}
          px={{ xs: 3, lg: 8 }}
        >
          <Typography
            component="p"
            variant="big"
            sx={{ textAlign: "center", mb: 3, color: "text.paper" }}
          >
            Sign in
          </Typography>
          {/*<Divider sx={{ marginBottom: 3, marginTop: 2 }} />*/}
          {/* {form.formState.errors?.afterSubmit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(form.formState.errors?.afterSubmit?.message)}
            </Alert>
          )} */}
          {user && !isAuthenticated ? (
            <Stack spacing={3}>
              {mode === "2fa" ? (
                <FizenInput
                  autoFocus
                  fullWidth
                  label="Enter 6-digit code"
                  // sx={{ height: 50 }}
                  value={digitCode}
                  onChange={handleChangeDigitCode}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && digitCode && digitCode.length === 6) {
                      handleConfirmCode();
                    }
                  }}
                  error={!!form.formState.errors?.code}
                  helperText={form.formState.errors?.code?.message}
                />
              ) : (
                <FizenInput
                  autoFocus
                  fullWidth
                  label="Enter 10-characters backup code"
                  // sx={{ height: 50 }}
                  value={backupCode}
                  onChange={handleChangeBackupCode}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && backupCode && backupCode.length === 10) {
                      handleConfirmCode();
                    }
                  }}
                  error={!!form.formState.errors?.code}
                  helperText={form.formState.errors?.code?.message}
                />
              )}
              <ButtonContained
                fullWidth
                onClick={handleConfirmCode}
                sx={{ height: 50 }}
                disabled={
                  !(mode === "2fa" && digitCode && digitCode.length === 6) &&
                  !(mode === "backup" && backupCode && backupCode.length === 10)
                }
              >
                Continue
              </ButtonContained>
            </Stack>
          ) : (
            <Form
              form={form}
              formModel={formModel}
              formButton={
                <div className="mt-3">
                  <FizenLink to={PATH_AUTH.resetPassword}>
                    <Typography variant="text" sx={{ mt: 3, color: "text.paper" }}>
                      Forgot password
                    </Typography>
                  </FizenLink>
                  <ButtonContained
                    fullWidth
                    sx={{ marginTop: 2.5, height: 50, textTransform: "none" }}
                    type={`submit`}
                    variant="contained"
                    disabled={false}
                  >
                    Sign in
                  </ButtonContained>
                </div>
              }
              // eslint-disable-next-line react/style-prop-object
              style="dark"
              initialValue={defaultValues}
              validationSchema={validationSchema}
              onFinish={onFirstLogin}
            />
          )}

          <Stack marginTop={3} spacing={1.5}>
            {user && !isAuthenticated ? (
              mode === "2fa" ? (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="text" sx={{ color: "text.paper" }}>
                    Don't have your phone?
                  </Typography>
                  <Typography
                    variant="text"
                    sx={{ color: "warning.dark", cursor: "pointer" }}
                    onClick={changeInputMode("backup")}
                  >
                    Enter a backup code
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="text" sx={{ color: "text.paper" }}>
                    Already sign in?
                  </Typography>
                  <Typography
                    variant="text"
                    sx={{ color: "warning.dark", cursor: "pointer" }}
                    onClick={changeInputMode("2fa")}
                  >
                    Enter 2FA code
                  </Typography>
                </Stack>
              )
            ) : (
              <>
                {/* <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}> */}
                <Typography variant="text" sx={{ color: "text.paper", textAlign: "center" }}>
                  Don't have an account?{" "}
                  <FizenLink to={PATH_AUTH.signUp}>
                    <Typography variant="text" sx={{ color: "warning.dark" }}>
                      Create an account?
                    </Typography>
                  </FizenLink>
                </Typography>

                {/* </Stack> */}
                {/*<Stack direction="row" justifyContent="space-between">*/}
                {/*  <Typography variant="text">Forgot your password?</Typography>*/}
                {/*  <FizenLink to={PATH_AUTH.resetPassword}>*/}
                {/*    <Typography variant="text">Reset your password</Typography>*/}
                {/*  </FizenLink>*/}
                {/*</Stack>*/}
              </>
            )}
          </Stack>
        </Box>
      </div>
    </Page>
  );
};

export default SignIn;
